import { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { login, loginForToken } from 'app/api';
import { useSelector } from 'react-redux';
import dayjs from 'app/libs/dayjs';
import { Base64 } from 'js-base64';
import { getSessionItem, removeSessionItem, setSessionItem } from 'app/libs/storageUtils';
import { MY_INFO_ACCESS_DATA_KEY, MY_INFO_SNS_TOKEN } from 'app/libs/auth';
import { addSnsSignOut, deleteSnsSignOut } from 'app/libs/snsUtils';
import useSsrCookie from 'app/hook/common/useSsrCookie';
import qs from 'query-string';
import errors from 'app/constants/errors';

export const getMyInfoAccessData = () => getSessionItem(MY_INFO_ACCESS_DATA_KEY);
export const setMyInfoAccessData = (data) => setSessionItem(MY_INFO_ACCESS_DATA_KEY, data);
export const removeMyInfoAccessData = () => removeSessionItem(MY_INFO_ACCESS_DATA_KEY);

function useMyInfoAccess() {
	const history = useHistory();
	const { pathname } = useLocation();
	const userInfoByToken = useSelector((state) => state.auth.userInfo) || {};
	const [isCheckedAccess, setIsCheckedAccess] = useState(false);
	const location = useLocation();
	const { setCookie } = useSsrCookie();

	const redirect = useCallback(
		(path) => {
			const firstPath = pathname.split('/')[1];

			if (firstPath === 'callback') {
				history.replace(path);
			} else {
				history.push(path);
			}
		},
		[history, pathname],
	);

	const handleSuccess = useCallback(
		(id, grade, platform) => {
			setMyInfoAccessData({
				id: Base64.encode(id),
				expired: dayjs().add(2, 'hours'),
				platform,
			});
			let nextPage;

			const { viewType, returnUrl } = qs.parse(location.search);

			if (grade === 'DEALER' || grade === 'REPEAT') {
				nextPage = `/my/info/certification?${qs.stringify({
					...(viewType && { viewType }),
				})}`;
			} else {
				nextPage = `/my/info/update?${qs.stringify({
					...(viewType && { viewType }),
					...(returnUrl && { returnUrl }),
				})}`;
			}

			redirect(nextPage);
		},
		[location.search, redirect],
	);

	const verifyPasswordForMyInfoAccess = useCallback(
		async (id, password, captchaKey) => {
			const { data } = await login(id, password, captchaKey);

			handleSuccess(data.user.id, data.user.grade, 'ENCAR');
		},
		[handleSuccess],
	);

	const verifySnsForMyInfoAccess = useCallback(
		async (token, platform) => {
			try {
				const { data } = await loginForToken(token, platform);

				if (userInfoByToken.id !== data.user.id) {
					alert('연동되지 않은 소셜 계정입니다.');
					addSnsSignOut(platform);
					redirect('/my/info/verify');
				} else {
					deleteSnsSignOut(platform);
					handleSuccess(data.user.id, data.user.grade, platform);

					setCookie(MY_INFO_SNS_TOKEN, token);
				}
			} catch (e) {
				if (e.response && e.response.data && e.response.data.code === errors.Code.User.NOT_FOUND_USER) {
					alert('연동되지 않은 소셜 계정입니다.');
				} else {
					alert('소셜계정 정보를 가져오지 못했습니다.');
				}

				addSnsSignOut(platform);
				redirect('/my/info/verify');
			}
		},
		[userInfoByToken.id, redirect, handleSuccess, setCookie],
	);

	const handleUnAuthorized = useCallback(() => {
		const { returnUrl } = qs.parse(location.search);

		removeSessionItem(MY_INFO_ACCESS_DATA_KEY);
		alert('회원님의 정보를 안전하게 보호하기 위해 인증이 필요합니다.');
		history.replace({
			pathname: '/my/info/verify',
			...(returnUrl && { search: `?returnUrl=${encodeURIComponent(returnUrl)}` }),
		});
	}, [history, location.search]);

	const checkAccessMyInfo = useCallback(
		async (identifyCheck) => {
			const infoAccessData = getMyInfoAccessData() || {};
			const id = Base64.decode(infoAccessData.id || '');
			const { expired, identityKey } = infoAccessData;
			const isExpired = dayjs().isAfter(expired);
			const isRequireIdentify = identifyCheck && !identityKey;

			if (!id || id !== userInfoByToken.id || isExpired || isRequireIdentify) {
				handleUnAuthorized();
			}

			setIsCheckedAccess(true);
		},
		[handleUnAuthorized, userInfoByToken.id],
	);

	return {
		verifyPasswordForMyInfoAccess,
		verifySnsForMyInfoAccess,
		checkAccessMyInfo,
		isCheckedAccess,
	};
}

export default useMyInfoAccess;
